import { useEffect, useRef, useState } from 'react';
import type { QuerySpec } from '@helios/powersync-client-core';
import { usePowerSyncDb } from './PowerSyncProvider';

/**
 * Executes a PowerSync query with real-time reactivity via `db.watch()`.
 *
 * - Watches `spec.watch` tables for changes
 * - Re-executes the query and yields new results immediately on table changes
 * - Stabilizes effect deps via JSON serialization to avoid spurious re-subscribes
 */
export function usePowerSyncQuery<TRow>(spec: QuerySpec) {
  const { db } = usePowerSyncDb();
  const [data, setData] = useState<TRow[]>([]);

  // Stabilize deps — spec is a new object each render
  const keyRef = useRef(JSON.stringify(spec.key));
  const sqlRef = useRef(spec.sql);
  const paramsRef = useRef(JSON.stringify(spec.params));
  const watchRef = useRef(JSON.stringify(spec.watch));

  const keyJson = JSON.stringify(spec.key);
  const paramsJson = JSON.stringify(spec.params);
  const watchJson = JSON.stringify(spec.watch);

  // Track if deps actually changed
  const depsChanged =
    keyJson !== keyRef.current ||
    spec.sql !== sqlRef.current ||
    paramsJson !== paramsRef.current ||
    watchJson !== watchRef.current;

  if (depsChanged) {
    keyRef.current = keyJson;
    sqlRef.current = spec.sql;
    paramsRef.current = paramsJson;
    watchRef.current = watchJson;
  }

  useEffect(() => {
    if (!db) return;

    const abortController = new AbortController();

    async function subscribe() {
      try {
        const params = JSON.parse(paramsRef.current);
        const tables = JSON.parse(watchRef.current) as string[];

        for await (const result of db!.watch(sqlRef.current, params, {
          tables,
          signal: abortController.signal,
        })) {
          setData((result.rows?._array ?? []) as TRow[]);
        }
      } catch {
        // AbortError is expected on cleanup
      }
    }

    subscribe();

    return () => {
      abortController.abort();
    };
  }, [db, keyRef.current, sqlRef.current, paramsRef.current, watchRef.current]);

  return { data };
}
